import type { QueryClient } from "@tanstack/react-query";
import { TrackerCellEdits } from "./trackerCellEdits";

/**
 * Tracker save state shared per query client, so it outlives the view: leaving and reopening
 * the tracker mid-save keeps one ordered queue per campaign and one view of pending edits.
 */
export class CampaignTrackerSaves {
  private static readonly byClient = new WeakMap<QueryClient, CampaignTrackerSaves>();

  public static for(queryClient: QueryClient): CampaignTrackerSaves {
    let saves = CampaignTrackerSaves.byClient.get(queryClient);
    if (!saves) {
      saves = new CampaignTrackerSaves();
      CampaignTrackerSaves.byClient.set(queryClient, saves);
    }
    return saves;
  }

  public readonly cellEdits = new TrackerCellEdits();
  private readonly queueByCampaign = new Map<string, Promise<void>>();
  private pendingCount = 0;

  /** Sends after the campaign's previous save settles, so the server stores edits in order. */
  public enqueue(campaignId: string, send: () => Promise<void>): Promise<void> {
    const previous = this.queueByCampaign.get(campaignId) ?? Promise.resolve();
    const current = previous.then(send);
    this.queueByCampaign.set(
      campaignId,
      current.catch(() => undefined),
    );
    return current;
  }

  /** Counts the work as pending until it settles; refreshes and focus refetches wait for zero. */
  public async track<T>(work: () => Promise<T>): Promise<T> {
    this.pendingCount += 1;
    try {
      return await work();
    } finally {
      this.pendingCount -= 1;
    }
  }

  public hasPending(): boolean {
    return this.pendingCount > 0;
  }
}
